import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useLang } from "../../contexts/LanguageContext";

const STORAGE_KEY = "pq_cookie_consent";

/**
 * GDPR-style cookie consent banner. Shown once per browser until the user
 * either accepts or declines — the choice is persisted in localStorage
 * (`pq_cookie_consent` = "accepted" | "declined").
 *
 * Because PrismQuery currently only uses strictly-necessary auth cookies and
 * a couple of localStorage prefs (theme/language/JWT), "Decline" doesn't
 * restrict app behaviour — it simply records the choice so we don't nag the
 * user again and could gate any future analytics/marketing cookies on it.
 */
export default function CookieConsent() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setOpen(true);
    } catch { /* localStorage blocked — banner stays hidden */ }
  }, []);

  const decide = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
      localStorage.setItem(`${STORAGE_KEY}_at`, new Date().toISOString());
    } catch { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("cookie.title")}
      className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-md z-[60] fade-in"
      data-testid="cookie-consent-banner"
    >
      <div className="relative rounded-lg border border-white/15 bg-[#0F0F0F]/95 backdrop-blur-xl shadow-2xl p-5">
        <button
          type="button"
          onClick={() => decide("dismissed")}
          className="absolute top-3 right-3 text-white/40 hover:text-white/80 transition-colors focus-ring rounded"
          aria-label={t("common.close")}
          data-testid="cookie-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-full bg-[#F5C542]/10 border border-[#F5C542]/40 flex items-center justify-center flex-shrink-0">
            <Cookie className="w-4 h-4 text-[#F5C542]" />
          </div>
          <div className="min-w-0">
            <div className="label-eyebrow text-[#F5C542] mb-1">{t("cookie.eyebrow")}</div>
            <h2 className="font-display font-black text-[17px] tracking-tight leading-tight">
              {t("cookie.title")}
            </h2>
          </div>
        </div>

        <p className="text-[13px] text-white/70 leading-relaxed mb-4">
          {t("cookie.body")}{" "}
          <Link
            to="/privacy"
            className="underline hover:text-white text-white/85"
            data-testid="cookie-privacy-link"
          >
            {t("cookie.privacyLink")}
          </Link>
          {" · "}
          <Link
            to="/terms"
            className="underline hover:text-white text-white/85"
            data-testid="cookie-terms-link"
          >
            {t("terms.link")}
          </Link>
          .
        </p>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => decide("accepted")}
            className="btn-pill focus-ring flex-1 justify-center"
            data-testid="cookie-accept-btn"
          >
            {t("cookie.accept")}
          </button>
          <button
            type="button"
            onClick={() => decide("declined")}
            className="btn-ghost focus-ring flex-1 justify-center"
            data-testid="cookie-decline-btn"
          >
            {t("cookie.decline")}
          </button>
        </div>
      </div>
    </div>
  );
}
